import { api } from '../services/api';
import React, { useState } from 'react';
import { Card, Btn, Alert, C } from './UI';
import { useBranding } from '../context/BrandingContext';

interface Props {
    jobId: string;
    estimatedUnits: number;
    actualUnits: number;
    originalPrice: number;
    adjustedPrice: number;
    // Carried over unchanged from the original booking-time computation — an adjustment only
    // ever changes the base unit price, never these. Included so "New total"/the Approve button
    // match what's actually captured (originalPrice already had these baked in; the raw
    // adjustedPrice alone doesn't).
    serviceFeeAmount?: number;
    taxAmount?: number;
    contractorNotes?: string;
    onApprove: () => void;
    onDispute: (reason: string) => void;
}

export function PriceAdjustmentModal({
    jobId, estimatedUnits, actualUnits,
    originalPrice, adjustedPrice, serviceFeeAmount = 0, taxAmount = 0, contractorNotes,
    onApprove, onDispute
}: Props) {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const [disputeReason, setDisputeReason] = useState('');
    const [showDispute, setShowDispute] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const difference = adjustedPrice - originalPrice;
    const newGrandTotal = adjustedPrice + serviceFeeAmount + taxAmount;

    const handleApprove = async () => {
        setLoading(true); setError('');
        try {
            await api.respondToPriceAdjustment(jobId, true);
            onApprove();
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const handleDispute = async () => {
        if (!disputeReason.trim()) { setError('Please explain your dispute.'); return; }
        setLoading(true); setError('');
        try {
            await api.respondToPriceAdjustment(jobId, false, disputeReason);
            onDispute(disputeReason);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 24
        }}>
            <Card style={{ width: '100%', maxWidth: 420 }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{ fontSize: 28 }}>📋</div>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 16 }}>Price adjustment request</h3>
                        <p style={{ margin: 0, fontSize: 13, color: C.textSub }}>
                            Your contractor counted more units than estimated
                        </p>
                    </div>
                </div>

                <Alert message={error} />

                {/* Comparison */}
                <div style={{ background: C.bg, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ textAlign: 'center', flex: 1 }}>
                            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>AI ESTIMATED</div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: C.textSub }}>{estimatedUnits}</div>
                            <div style={{ fontSize: 11, color: C.textMuted }}>units</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', color: C.textMuted, fontSize: 20 }}>→</div>
                        <div style={{ textAlign: 'center', flex: 1 }}>
                            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>ACTUAL COUNT</div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: '#C0392B' }}>{actualUnits}</div>
                            <div style={{ fontSize: 11, color: C.textMuted }}>units</div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: C.textSub }}>Original price</span>
                        <span style={{ fontSize: 13, fontWeight: 600, textDecoration: 'line-through', color: C.textMuted }}>${originalPrice.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: C.textSub }}>Additional charge</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#C0392B' }}>+${difference.toFixed(2)}</span>
                    </div>
                    {serviceFeeAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontSize: 13, color: C.textSub }}>Service fee</span>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>${serviceFeeAmount.toFixed(2)}</span>
                        </div>
                    )}
                    {taxAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontSize: 13, color: C.textSub }}>Tax</span>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>${taxAmount.toFixed(2)}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                        <span style={{ fontSize: 15, fontWeight: 700 }}>New total</span>
                        <span style={{ fontSize: 20, fontWeight: 700, color: C.green }}>${newGrandTotal.toFixed(2)}</span>
                    </div>
                </div>

                {contractorNotes && (
                    <div style={{ background: '#FAEEDA', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#854F0B' }}>
                        💬 <strong>{contractorLabel} note:</strong> {contractorNotes}
                    </div>
                )}

                {!showDispute ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Btn onClick={handleApprove} disabled={loading} fullWidth size="lg">
                            {loading ? 'Processing...' : `✓ Approve — $${newGrandTotal.toFixed(2)}`}
                        </Btn>
                        <Btn variant="outline" onClick={() => setShowDispute(true)} fullWidth>
                            ✗ Dispute this charge
                        </Btn>
                        <div style={{ fontSize: 11, color: C.textMuted, textAlign: 'center' }}>
                            Your contractor is on-site waiting for your response
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 5 }}>
                                Why are you disputing this charge?
                            </label>
                            <textarea
                                value={disputeReason}
                                onChange={e => setDisputeReason(e.target.value)}
                                rows={3}
                                placeholder="e.g. I only had 8 boxes, not 15..."
                                style={{
                                    width: '100%', padding: '10px 12px',
                                    border: `1px solid ${C.border}`, borderRadius: 8,
                                    fontSize: 13, fontFamily: 'Inter, sans-serif',
                                    resize: 'vertical', boxSizing: 'border-box' as const
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={() => setShowDispute(false)}>Back</Btn>
                            <Btn variant="danger" onClick={handleDispute} disabled={loading} fullWidth>
                                {loading ? 'Submitting...' : 'Submit dispute'}
                            </Btn>
                        </div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8, textAlign: 'center' }}>
                            An admin will review your dispute and resolve it
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
